import { z } from 'zod';
import rawVocabulary from '../../data/visual-style-labels.v1.json';

export const visualStyleFamilies = [
  'aesthetic_reference',
  'form_and_composition',
  'visual_density',
  'material_and_finish',
  'colour_and_light',
  'mood_and_atmosphere',
  'fashion_and_presentation',
  'spatial_character',
  'temporal_reference',
  'craft_and_detail',
] as const;

const styleLabelSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  label: z.string().min(2).max(48),
  shortDescription: z.string().min(12).max(180),
  family: z.enum(visualStyleFamilies),
  aliases: z.array(z.string().min(2).max(48)),
  relatedLabelIds: z.array(z.string()),
  conflictingLabelIds: z.array(z.string()),
  genericness: z.number().min(0).max(1),
  distinctiveness: z.number().min(0).max(1),
});

const vocabularySchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  labels: z.array(styleLabelSchema).min(130).max(180),
});

export const visualStyleVocabulary = vocabularySchema.parse(rawVocabulary);
export type VisualStyleLabel = z.infer<typeof styleLabelSchema>;
export type VisualStyleFamily = (typeof visualStyleFamilies)[number];

const ids = visualStyleVocabulary.labels.map((item) => item.id);
const idSet = new Set(ids);
const labelById = new Map(visualStyleVocabulary.labels.map((item) => [item.id, item]));
const names = new Map<string, string>();

for (const item of visualStyleVocabulary.labels) {
  if (names.has(item.label.toLocaleLowerCase())) throw new Error(`Duplicate style label: ${item.label}`);
  names.set(item.label.toLocaleLowerCase(), item.id);
  for (const alias of item.aliases) {
    const key = alias.toLocaleLowerCase();
    if (names.has(key) || idSet.has(key.replaceAll(' ', '_'))) {
      throw new Error(`Style-label alias collision: ${alias}`);
    }
    names.set(key, item.id);
  }
  for (const reference of [...item.relatedLabelIds, ...item.conflictingLabelIds]) {
    if (!idSet.has(reference)) throw new Error(`Unknown style-label reference: ${item.id} -> ${reference}`);
  }
}

for (const family of visualStyleFamilies) {
  if (!visualStyleVocabulary.labels.some((item) => item.family === family)) {
    throw new Error(`Style-label family has no coverage: ${family}`);
  }
}

export const styleLabelIds = ids as [string, ...string[]];
export const visualStyleLabelIdSchema = z.enum(styleLabelIds);
export type VisualStyleLabelId = z.infer<typeof visualStyleLabelIdSchema>;

export const getVisualStyleLabel = (id: string) => labelById.get(id);
export const isVisualStyleLabelId = (id: string): id is VisualStyleLabelId => idSet.has(id);

export const searchVisualStyleLabels = (query: string, limit = 24) => {
  const needle = query.toLocaleLowerCase().trim();
  const ranked = visualStyleVocabulary.labels
    .map((item) => {
      const searchable = [item.label, item.shortDescription, ...item.aliases]
        .join(' ')
        .toLocaleLowerCase();
      const score = !needle
        ? item.distinctiveness
        : item.label.toLocaleLowerCase().startsWith(needle)
          ? 3
          : searchable.includes(needle)
            ? 2
            : item.family.replaceAll('_', ' ').includes(needle)
              ? 1
              : 0;
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score || right.item.distinctiveness - left.item.distinctiveness);
  return ranked.slice(0, limit).map(({ item }) => item);
};

export const compactVisualVocabularyForPrompt = () =>
  visualStyleVocabulary.labels
    .map((item) => `${item.id}: ${item.label} — ${item.shortDescription}`)
    .join('\n');

